import React, { useEffect, useMemo, useRef, useState } from 'react'
import { debounce, isEqual } from 'lodash'

import environment from 'environment'

import { useActionless, useActions, useAppSelector } from 'redux/hooks'
import { SET_COLLECTION_V2_STATE } from 'redux/collectionV2/actions/types'
import { modifyCollectionCardMapAction } from 'redux/collectionV2/actions'

import { CollectionV2Card } from 'redux/collectionV2/types'
import { DOUBLE_FACED_LAYOUTS } from 'types/card'

import ToastService from 'services/toast.service'

import { basicKeybindSkips } from 'components/deckPage/modals/KeybindsOverlay'
import CollectionService from 'services/collection.service'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'

export type CardActions = {
  multiSelect: () => void
  cardClick: (e: React.MouseEvent, skipOpenOnClick?: boolean) => void
  rightClick: (e: React.MouseEvent) => void
  openCardModal: (view?: 'details' | 'relatedCards' | 'decks', openToAllEditions?: boolean) => void
  incrementCard: () => void
  decrementCard: () => void
  copyCardName: () => void
  setNormal: () => void
  setFoil: () => void
  setEtched: () => void
  setFlipped: (flipped: boolean) => void
  setLocalQuantity: (newQuantity: number) => void
  deleteCard: () => void
  onCardUpdate: (updatedCard: CollectionV2Card | CollectionV2Card[], removedId?: number) => void
}

export type LocalCollectionV2CardData = {
  quantity: number
  flipped: boolean
  anyMultiSelected: boolean
  isMultiSelected: boolean
  hasFinishOptions: boolean
  isNormal: boolean
  isFoil: boolean
  isEtched: boolean
  hasFoilOption: boolean
  hasEtchedOption: boolean
  isFlipCard: boolean
  ownsCollection: boolean
  loading: boolean
}

export type CollectionCardWrapperChildProps = {
  collectionCard: CollectionV2Card
  localCardData: LocalCollectionV2CardData
  actions: CardActions
  extraData: Record<string, any>
}

type Props = {
  collectionCardId: number
  component: React.FC<CollectionCardWrapperChildProps>
  extraData?: Record<string, any>
}

const CardWrapper = ({ component, collectionCardId, extraData = {} }: Props) => {
  const game = useAppSelector(state => state.collectionV2.game)
  const collectionCard = useAppSelector(state => state.collectionV2.collectionCards[collectionCardId])
  const ownsCollection = useAppSelector(state => state.collectionV2.ownsCollection)

  const multiSelectedIds = useAppSelector(state => state.collectionV2.multiSelectedIds)

  const [setCollectionV2State] = useActionless(SET_COLLECTION_V2_STATE)
  const [modifyCollectionCardMap] = useActions(modifyCollectionCardMapAction)

  const [flipped, setFlipped] = useState(false)
  const [localQuantity, setLocalQuantity] = useState(collectionCard.quantity) // used while debouncing fast quantity changes
  const [loading, setLoading] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const touched = useRef<boolean>(false)
  const keylistener = useRef<(e: KeyboardEvent) => void>(() => null)
  const hovered = useRef<boolean>(false)

  const hasFinishOptions = collectionCard.card.options.length > 1
  const isNormal = collectionCard.modifier === 'Normal'
  const isFoil = collectionCard.modifier === 'Foil'
  const isEtched = collectionCard.modifier === 'Etched'
  const hasFoilOption = collectionCard.card.options.includes('Foil')
  const hasEtchedOption = collectionCard.card.options.includes('Etched')

  const isFlipCard = DOUBLE_FACED_LAYOUTS.includes(collectionCard.card.layout)

  const anyMultiSelected = !!Object.keys(multiSelectedIds).length
  const isMultiSelected = !!multiSelectedIds[collectionCard.id]

  const handleDeleteCard = () => {
    if (!ownsCollection) return Promise.resolve(() => modifyCollectionCardMap(collectionCard, collectionCard.id)) // Retruning promise for typing reasons

    setConfirmDelete(false)

    return CollectionService.deleteCard(collectionCard.id)
      .then(() => modifyCollectionCardMap(collectionCard, collectionCard.id))
      .catch(() => ToastService.create('Error deleting card'))
  }

  const handleUpdateCard = (updatedCollectionCard: CollectionV2Card | CollectionV2Card[], removedId?: number) => {
    // prettier-ignore
    if (Array.isArray(updatedCollectionCard)) return ToastService.create('Cannot update multiple cards at once this way, we must have missed something. If this continues to happen please let the Archdiekt team know.', 'Collection page', 'error')

    if (!collectionCard) return

    if (isEqual(updatedCollectionCard, collectionCard)) return

    if (removedId) return handleDeleteCard()

    if (!ownsCollection) return modifyCollectionCardMap(updatedCollectionCard)

    setLoading(true)

    if (updatedCollectionCard.quantity < 1) {
      return CollectionService.deleteCard(updatedCollectionCard.id)
        .then(() => {
          modifyCollectionCardMap(updatedCollectionCard, updatedCollectionCard.id)
          setCollectionV2State({ activeCardId: null })

          ToastService.create(`${updatedCollectionCard.card.name} deleted`, 'Collection page', 'success')
        })
        .catch(() =>
          ToastService.create(`${updatedCollectionCard.card.name} delete failed`, 'Collection page', 'error'),
        )
    }

    CollectionService.upsertV2(updatedCollectionCard, game)
      .then(responseCard => {
        modifyCollectionCardMap({
          ...updatedCollectionCard,
          modifiedAt: responseCard.modifiedAt,
          createdAt: responseCard.createdAt,
        })

        ToastService.create(`${updatedCollectionCard.card.name} saved`, 'Collection page', 'success')
      })
      .catch(() => ToastService.create(`${updatedCollectionCard.card.name} save failed`, 'Collection page', 'error'))
      .finally(() => setLoading(false))
  }

  const handleMultiSelect = () => {
    const updatedMultiSelectedIds = { ...multiSelectedIds }

    if (isMultiSelected) delete updatedMultiSelectedIds[collectionCard.id]
    else updatedMultiSelectedIds[collectionCard.id] = true

    setCollectionV2State({ multiSelectedIds: updatedMultiSelectedIds })
  }

  const handleCardClick = (e: React.MouseEvent, skipOpenOnClick?: boolean) => {
    const modKeyHeld = environment.getOnMac() ? e.metaKey : e.ctrlKey

    if (!modKeyHeld && !anyMultiSelected && skipOpenOnClick) return
    if (modKeyHeld || anyMultiSelected) return handleMultiSelect()

    setCollectionV2State({ activeCardId: collectionCard.id })
  }

  const handleRightClick = (e: React.MouseEvent) => {
    // Only supported on mobile
    if (!touched.current) return

    e.preventDefault()
    handleMultiSelect()
  }

  const handleSetDebouncedQty = (quantity: number) => {
    const removeId = quantity < 1 ? collectionCard.id : undefined
    const updatedCard = { ...collectionCard, quantity }

    handleUpdateCard(updatedCard, removeId)
  }

  // Reset local state if the cards quantity changes someway that isn't here
  // Reset the debounce method to make sure it has correct card info (since it's only updated when lodash.debounce is called)
  const debouncedQtyChange = useMemo(() => {
    if (collectionCard.quantity !== localQuantity) setLocalQuantity(collectionCard.quantity)

    return debounce(handleSetDebouncedQty, 660)
  }, [collectionCard])

  const handleIncrementCard = () => {
    setLocalQuantity(localQuantity + 1)
    debouncedQtyChange(localQuantity + 1)
  }

  const handleDecrementCard = () => {
    setLocalQuantity(localQuantity - 1 > 0 ? localQuantity - 1 : 0)
    debouncedQtyChange(localQuantity - 1)
  }

  const handleCopyCardName = () => {
    navigator.clipboard.writeText(collectionCard.card.name)
    ToastService.create(`Copied ${collectionCard.card.name} to clipboard`, 'Deck Page', 'success')
  }

  const handleSetNormal = () => handleUpdateCard({ ...collectionCard, modifier: 'Normal' })
  const handleSetFoil = () => handleUpdateCard({ ...collectionCard, modifier: 'Foil' })
  const handleSetEtched = () => handleUpdateCard({ ...collectionCard, modifier: 'Etched' })

  useEffect(() => {
    // Cleanup old listeners (we need to remove the old ones and create new ones when the state changes)
    window.removeEventListener('keydown', keylistener.current)

    // Update current listener (this way the listener has the updated redux/ local state)
    keylistener.current = (e: KeyboardEvent) => {
      if (!hovered.current) return

      const key = e.key.toLocaleUpperCase()

      // prettier-ignore
      const listenedForKeys = ['-', '_', '=', '+', 'R', 'F', 'N', 'E', 'L', 'O', 'P'  ]

      if (e.ctrlKey || e.metaKey) return
      if (basicKeybindSkips(e)) return
      if (!listenedForKeys.includes(key)) return
      if (anyMultiSelected)
        return ToastService.create('Keybinds are disabled when multiselecting', 'Collection page', 'warning')

      if (isFlipCard && key === 'L') setFlipped(!flipped)
      if (key === 'O') setCollectionV2State({ activeCardId: collectionCard.id })
      if (key === 'P') setCollectionV2State({ activeCardId: collectionCard.id })

      if (!ownsCollection) return // the rest of the actions deal with editing the deck, if they don't have write access, we can ignore the rest

      if (key === '-' || key === '_') handleDecrementCard()
      if (key === '=' || key === '+') handleIncrementCard()
      if (key === 'R') setConfirmDelete(true)

      if (!isFoil && hasFoilOption && key === 'F') handleSetFoil()
      if (!isNormal && hasFinishOptions && key === 'N') handleSetNormal()
      if (!isEtched && hasEtchedOption && key === 'E') handleSetEtched()
    }

    window.addEventListener('keydown', keylistener.current)

    return () => window.removeEventListener('keydown', keylistener.current)
  }, [localQuantity, collectionCard, flipped, ownsCollection, anyMultiSelected])

  const actions: CardActions = {
    multiSelect: handleMultiSelect,
    cardClick: handleCardClick,
    rightClick: handleRightClick,
    incrementCard: handleIncrementCard,
    decrementCard: handleDecrementCard,
    copyCardName: handleCopyCardName,
    // prettier-ignore
    openCardModal: (activeCardTab = 'details', openToAllEditionsMenu = false) => setCollectionV2State({ activeCardId: collectionCard.id, activeCardTab, openToAllEditionsMenu }),
    setNormal: handleSetNormal,
    setFoil: handleSetFoil,
    setEtched: handleSetEtched,
    setFlipped,
    setLocalQuantity,
    deleteCard: () => setConfirmDelete(true),
    onCardUpdate: handleUpdateCard,
  }

  const cardData: LocalCollectionV2CardData = {
    quantity: localQuantity,
    flipped,
    anyMultiSelected,
    isMultiSelected,
    hasFinishOptions,
    isNormal,
    isFoil,
    isEtched,
    hasFoilOption,
    hasEtchedOption,
    isFlipCard,
    loading,
    ownsCollection: ownsCollection,
  }

  const Component = component

  return (
    <>
      <div
        onMouseEnter={() => (hovered.current = true)}
        onMouseLeave={() => (hovered.current = false)}
        onTouchStart={() => (touched.current = true)}
        onTouchEnd={() => (touched.current = false)}>
        <Component collectionCard={collectionCard} actions={actions} localCardData={cardData} extraData={extraData} />
      </div>

      <ConfirmDeleteModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onDelete={handleDeleteCard}
        header="Delete card"
        message={`Are you sure you want to delete ${collectionCard.card.name} from your collection?`}
        buttonText="Delete card"
        loadingMessage="Deleting card..."
      />
    </>
  )
}
export default CardWrapper
